import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, Youtube, MessageCircle } from "lucide-react";
import GoldSeam from "@/components/GoldSeam";
import { waLink, WHATSAPP_DISPLAY, OWNER_EMAIL } from "@/lib/site";

const PinterestIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.739.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
  </svg>
);

const SOCIALS = [
  { label: "Instagram", href: "https://www.instagram.com/talkbyrajdeep/", Icon: Instagram },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/rajdeep-brar-a4627a274/", Icon: Linkedin },
  { label: "Pinterest", href: "https://in.pinterest.com/brarrajdeep369/", Icon: PinterestIcon },
  { label: "YouTube", href: "https://www.youtube.com/@TheanaloglifeKintsuki", Icon: Youtube },
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61593803769228", Icon: Facebook },
  { label: "WhatsApp", href: waLink(), Icon: MessageCircle },
];

const LINKS = [
  { label: "Services", to: "/services" },
  { label: "Book a Session", to: "/booking" },
  { label: "Approach", to: "/approach" },
  { label: "About", to: "/about" },
  { label: "Letters", to: "/letters" },
  { label: "FAQ & Terms", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export default function Footer() {
  return (
    <footer className="relative bg-paper border-t border-[rgba(169,129,47,0.26)]" data-testid="site-footer">
      <GoldSeam variant="horizontal" className="w-full h-16 opacity-60" delay={0.1} />
      <div className="max-w-7xl mx-auto px-6 pb-14 pt-6">
        <div className="grid md:grid-cols-3 gap-12 items-start">
          <div>
            <img
              src="/images/logo-lockup.png"
              alt="T.A.L.K. — The Analog Life Kintsugi"
              className="w-60 -ml-2 mb-6"
              data-testid="footer-logo"
            />
            <p className="font-display italic text-goldink text-xl leading-relaxed" data-testid="footer-tagline">
              Where broken becomes <span className="text-gold">gold</span>.
            </p>
          </div>
          <nav className="grid grid-cols-2 gap-x-6 gap-y-3 md:pt-2" aria-label="Footer">
            {LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`footer-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                className="gold-underline-link self-start text-[10px] uppercase tracking-[0.24em] text-inkdim hover:text-gold"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="md:pt-2">
            <p className="eyebrow mb-4">Reach out</p>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="gold-underline-link block text-sm text-ink"
              data-testid="footer-whatsapp-link"
            >
              WhatsApp {WHATSAPP_DISPLAY}
            </a>
            <a
              href={`mailto:${OWNER_EMAIL}`}
              className="gold-underline-link block mt-3 text-sm text-ink"
              data-testid="footer-email-link"
            >
              {OWNER_EMAIL}
            </a>
            <div className="flex flex-wrap gap-3 mt-7">
              {SOCIALS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}-link`}
                  className="w-10 h-10 border border-[rgba(169,129,47,0.26)] flex items-center justify-center text-inkdim hover:text-gold hover:border-gold transition-colors duration-300"
                >
                  <Icon size={17} strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-14 pt-7 border-t border-[rgba(169,129,47,0.26)] flex flex-col md:flex-row md:items-center justify-between gap-4">
          <p className="text-[10px] uppercase tracking-[0.3em] text-inkdim" data-testid="footer-stamp">
            T.A.L.K. — The Analog Life Kintsugi
          </p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-inkdim">
            Designed by{" "}
            <a
              href="https://thesilver-tree.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-credit-link"
              className="gold-underline-link text-gold"
            >
              Silvertree Design Studio
            </a>
          </p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-inkdim">
            talkbyrajdeep.com · © 2026
          </p>
        </div>
      </div>
    </footer>
  );
}
